import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

type SortField = 'date' | 'score' | 'device' | 'location' | 'status';
type SortDirection = 'asc' | 'desc';

interface TableHeaderProps {
  sortField: SortField;
  sortDirection: SortDirection;
  handleSort: (field: SortField) => void;
  variant: 1 | 2 | 3 | 4 | 5;
}

export const TableHeaderVariants: React.FC<TableHeaderProps> = ({
  sortField,
  sortDirection,
  handleSort,
  variant
}) => {
  const { t } = useTranslation();
  // Variant configurations
  const variants = {
    1: {
      name: "Roșu accent puternic",
      theadClass: "bg-regina-red",
      thClass: "px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-regina-red-600 transition-colors border-r border-red-800",
      thLastClass: "px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-regina-red-600 transition-colors"
    },
    2: {
      name: "Roșu Deschis cu Text Roșu",
      theadClass: "bg-regina-red-50",
      thClass: "px-6 py-4 text-left text-xs font-semibold text-regina-red uppercase tracking-wider cursor-pointer hover:bg-red-100 transition-colors border-r border-red-200",
      thLastClass: "px-6 py-4 text-left text-xs font-semibold text-regina-red uppercase tracking-wider cursor-pointer hover:bg-red-100 transition-colors"
    },
    3: {
      name: "Gri Închis Profesional",
      theadClass: "bg-regina-gray-700",
      thClass: "px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-regina-gray-800 transition-colors border-r border-gray-600",
      thLastClass: "px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-regina-gray-800 transition-colors"
    },
    4: {
      name: "White cu Bordură Roșie",
      theadClass: "bg-white border-b-4 border-regina-red",
      thClass: "px-6 py-4 text-left text-xs font-semibold text-regina-gray-800 uppercase tracking-wider cursor-pointer hover:bg-regina-red-50 transition-colors border-r border-regina-gray-300",
      thLastClass: "px-6 py-4 text-left text-xs font-semibold text-regina-gray-800 uppercase tracking-wider cursor-pointer hover:bg-regina-red-50 transition-colors"
    },
    5: {
      name: "Roșu Deschis Solid",
      theadClass: "bg-regina-red-50",
      thClass: "px-6 py-4 text-left text-xs font-semibold text-regina-red uppercase tracking-wider cursor-pointer hover:bg-red-100 transition-colors border-r border-red-200",
      thLastClass: "px-6 py-4 text-left text-xs font-semibold text-regina-red uppercase tracking-wider cursor-pointer hover:bg-red-100 transition-colors"
    }
  };

  const config = variants[variant];

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUpIcon className="w-4 h-4" /> : 
      <ChevronDownIcon className="w-4 h-4" />;
  };

  return (
    <thead className={config.theadClass}>
      <tr>
        <th 
          onClick={() => handleSort('score')}
          className={config.thClass}
        >
          <div className="flex items-center space-x-1">
            <span>{t('table.score')}</span>
            <SortIcon field="score" />
          </div>
        </th>
        <th 
          onClick={() => handleSort('date')}
          className={config.thClass}
        >
          <div className="flex items-center space-x-1">
            <span>{t('table.date')}</span>
            <SortIcon field="date" />
          </div>
        </th>
        <th 
          onClick={() => handleSort('status')}
          className={config.thClass}
        >
          <div className="flex items-center space-x-1">
            <span>{t('table.status')}</span>
            <SortIcon field="status" />
          </div>
        </th>
        <th 
          onClick={() => handleSort('location')}
          className={config.thClass}
        >
          <div className="flex items-center space-x-1">
            <span>{t('table.location')}</span>
            <SortIcon field="location" />
          </div>
        </th>
        <th 
          onClick={() => handleSort('device')}
          className={config.thLastClass}
        >
          <div className="flex items-center space-x-1">
            <span>{t('table.device')}</span>
            <SortIcon field="device" />
          </div>
        </th>
      </tr>
    </thead>
  );
};

// Variant selector component
export const VariantSelector: React.FC<{
  currentVariant: 1 | 2 | 3 | 4 | 5;
  onVariantChange: (variant: 1 | 2 | 3 | 4 | 5) => void;
}> = ({ currentVariant, onVariantChange }) => {
  const variants = [
    { id: 1, name: "Roșu Bold", emoji: "🔴" },
    { id: 2, name: "Roșu Subtil", emoji: "🔴" },
    { id: 3, name: "Gri Închis", emoji: "⬛" },
    { id: 4, name: "White + Border", emoji: "⬜" },
    { id: 5, name: "Roșu Solid", emoji: "🔴" }
  ];

  return (
    <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-semibold text-yellow-800">🎨 TEST VARIANTE HEADER:</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {variants.map(v => (
            <button
              key={v.id}
              onClick={() => onVariantChange(v.id as 1 | 2 | 3 | 4 | 5)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                currentVariant === v.id
                  ? 'bg-regina-red text-white shadow-md'
                  : 'bg-white text-regina-gray-800 border border-regina-gray-300 hover:bg-regina-gray-50'
              }`}
            >
              {v.emoji} {v.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

